// START IMPORTS
// Theme and UI imports
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  DateUtil,
  isZeroed,
  nameize,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  useSession,
  ReportLoading,
  ReportOptions,
  ReportFactory,
  ReportNullState,
  ReportMonitor,
  flattenBonusData,
  ReportChangesOverview,
  ObituaryDetails,
  BurialInformation,
  ObituaryBanner,
  DebugMenu,
  Owner,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  AddressHistorySection,
  AssetsSection,
  AssociatesSection,
  BankruptcySection,
  CourtRecords,
  CourtRecordsLoadingScreen,
  CriminalOrTrafficSection,
  EducationSection,
  EmailSection,
  JobsSection,
  LicensesAndPermitsSection,
  MaritalRecordsSection,
  MonitoringBannerSection,
  NameAncestrySection,
  NeighborsSection,
  NotesSection,
  PersonReportOverview,
  PhoneSection,
  RelatedReportsSection,
  RelativesSection,
  ReportActionsWithDateUpdated,
  ReportClaimingSection,
  ReportNavigationMenu,
  ReportNavigationMenuProps,
  ReportNavigationMobileMenu,
  ReportRating,
  SocialSection,
  PdfSection,
  ReportAttachmentsCTA,
  ReportAttachmentsSection,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import {
  getPersonNavLinkData,
  getPersonNavLinkDataV2,
} from 'navLinkData/personNavLinkData';

// Asset imports
import imgObituarySearch from '../../images/img_obituary-search.svg';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PersonReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'person';
// END CONSTANTS

export function PersonReportPage({
  permalink,
  isMonitored: _isMonitored = false,
}: PersonReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();
  const queryClient = useQueryClient();

  const queryResult = useReport(permalink);
  const { currentMonitors, reportIsMonitored } = queryResult;

  const currentUserInfo = account?.account?.user_info;

  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);

  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1579');
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [showBankruptcyData] = useState<boolean>(false);
  const [showCriminalData, setShowCriminalData] = useState(false);
  const [isCourtRecordsLoading, setIsCourtRecordsLoading] = useState(false);

  const [reportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });

  const [activeClaiming, setActiveClaiming] = useState(false);
  const [disableClaiming, setDisableClaiming] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const alternativeTitle = isInPartialAddressesVariation
    ? 'Possible Locations'
    : undefined;
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    setDisableClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return (
          monitor.report_type === 'detailed_person_report' &&
          monitor.is_claimed &&
          permalink !== monitor.permalink
        );
      })
    );
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);
  // END EFFECTS

  // START EARLY_RETURNS
  const hideCriminalBJLData = account?.account.user_settings
    .hide_criminal_records as boolean;

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={14} />;
  }

  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'person');

  let showReportClaiming = false;
  if (
    currentUserInfo?.first_name &&
    currentUserInfo.last_name &&
    report.personName.includes(currentUserInfo.first_name) &&
    report.personName.includes(currentUserInfo.last_name)
  ) {
    showReportClaiming = true;
  }

  const MonitoringComponent =
    showReportClaiming && !disableClaiming ? (
      <ReportClaimingSection
        reportPermaLink={permalink}
        reportType={reportType}
        reportIsMonitoredAndClaimed={activeClaiming}
      />
    ) : reportIsMonitored ? null : (
      <MonitoringBannerSection
        reportPermaLink={permalink}
        reportType={reportType}
      />
    );

  if (!report) return <ReportLoading menuItems={14} />;

  if (!report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const reportUpgraded = queryResult.data?.meta?.report_upgraded ?? false;
  const revealFreeBonusData = showCriminalData && !isCourtRecordsLoading;

  const owner = report.getOwner(reportOptions);
  const {
    ownerName,
    nameInfo,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    ownedAssets,
    marital,
    relatives,
    associates,
    neighbors,
    courts,
    obituary,
    grave,
    bonusData,
  } = owner;
  const flattenedBonusData = flattenBonusData(bonusData);

  const personNavLinkData = getPersonNavLinkData(
    owner as Owner,
    showCriminalData,
    showBankruptcyData,
    hideCriminalBJLData,
    reportUpgraded,
    flattenedBonusData.length,
    relatives || []
  );

  const personNavLinkDataV2 = getPersonNavLinkDataV2(
    owner as Owner,
    hideCriminalBJLData,
    revealFreeBonusData,
    relatives || []
  );

  document.title = `${report.personName} - PeopleLooker`;

  const dob = owner.identity?.dobs?.[0]?.date?.parsed?.year?.toString() ?? '';
  const dod = owner.identity?.dods[0]?.date?.full || '';
  const isDeceased = !!dod;
  const shouldShowObituarySection = !!obituary?.funeralHome;
  const shouldShowObituaryCta = isDeceased && !shouldShowObituarySection;

  document.title = `${report.personName} - PeopleLooker`;

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  const randomDelayForCourtRecords = Math.random() * 5000;

  function handleCourtRecordsLoadingScreen() {
    setIsCourtRecordsLoading(true);
    queryClient.invalidateQueries({
      queryKey: ['report', permalink],
    });
    queryClient.refetchQueries({
      queryKey: ['report', permalink],
    });
    setTimeout(() => {
      setIsCourtRecordsLoading(false);
      setShowCriminalData(true);
    }, randomDelayForCourtRecords * 6);
  }
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction={'row'}>
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Person Report"
            headerTitle={ownerName}
            menuItems={
              personNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          {reportIsMonitored && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={personNavLinkData}
            />
          )}
          <Box
            sx={{
              height: 30,
              backgroundColor: 'background.default',
              position: 'sticky',
              marginBottom: '-10px',
              top: 52,
              zIndex: 10,
              display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Person"
            dateUpdated={reportUpdateDate}
            reportUpgraded={reportUpgraded}
          />
          <PersonReportOverview
            reportType={reportType}
            owner={owner as Owner}
            personName={ownerName}
            personOverviewItemsData={{
              hideCriminalBJLData,
              phones,
              emails,
              addresses,
              relatives,
              images: owner!.images,
              profiles,
              usernames,
              jobs,
              educations,
            }}
            dateUpdated={reportUpdateDate}
            reportUpgraded={revealFreeBonusData}
          />

          <ReportAttachmentsCTA
            userCode={currentUserInfo?.user_code || ''}
            permalink={permalink}
            reportType={reportType}
          />

          {MonitoringComponent}

          {shouldShowObituaryCta && (
            <ObituaryBanner image={imgObituarySearch} />
          )}

          <PdfSection permalink={permalink} shouldFireGAUpsell />

          <PhoneSection
            personName={ownerName}
            phoneList={phones}
            permalink={permalink}
          />

          <EmailSection
            personName={ownerName}
            emailList={emails}
            permalink={permalink}
          />

          <AddressHistorySection
            addresses={addresses}
            personName={ownerName}
            permalink={permalink}
            alternateTitle={alternativeTitle}
          />

          <SocialSection
            personName={ownerName}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />

          <NameAncestrySection
            firstName={nameize(nameInfo?.parsed?.first ?? null)}
            surName={nameize(nameInfo?.parsed?.last ?? null)}
            yearOfBirth={dob}
          />

          <RelativesSection
            personName={ownerName}
            relatives={relatives || []}
            permalink={permalink}
          />

          <>
            {shouldShowObituarySection && (
              <ObituaryDetails obituary={obituary} permalink={permalink} />
            )}
            {grave && <BurialInformation grave={grave} permalink={permalink} />}
          </>

          <MaritalRecordsSection
            personName={ownerName}
            marital={marital}
            permalink={permalink}
          />

          <AssociatesSection
            personName={ownerName}
            associates={associates || []}
            permalink={permalink}
          />

          <NeighborsSection
            personName={ownerName}
            neighbors={neighbors || []}
            permalink={permalink}
          />

          {!hideCriminalBJLData && (
            <>
              {revealFreeBonusData ? (
                <>
                  <CriminalOrTrafficSection
                    criminalOrTrafficData={courts.criminal || []}
                    personName={ownerName}
                  />
                  <BankruptcySection
                    personName={ownerName}
                    bankruptcyData={courts.bankruptcy || []}
                  />
                  <LicensesAndPermitsSection
                    personName={ownerName}
                    bonusData={bonusData}
                  />
                </>
              ) : (
                <>
                  {isCourtRecordsLoading ? (
                    <CourtRecordsLoadingScreen
                      personName={ownerName}
                      delayDuration={randomDelayForCourtRecords}
                    />
                  ) : (
                    <CourtRecords
                      handleCourtRecordsLoadingScreen={
                        handleCourtRecordsLoadingScreen
                      }
                    />
                  )}
                </>
              )}
            </>
          )}

          <JobsSection
            personName={ownerName}
            jobs={jobs}
            permalink={permalink}
          />

          <EducationSection
            personName={ownerName}
            educations={educations}
            permalink={permalink}
          />

          <AssetsSection
            personName={ownerName}
            ownedAssets={ownedAssets}
            permalink={permalink}
          />

          <Box id="attachments-section">
            <ReportAttachmentsSection
              userCode={currentUserInfo?.user_code || ''}
              permalink={permalink}
              reportType={reportType}
            />
          </Box>

          <NotesSection permalink={permalink} />

          <RelatedReportsSection
            reportType="person"
            personName={ownerName}
            potentialOwners={report.data.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />

          <ReportRating
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Person Report"
        headerTitle={ownerName}
        menuItems={
          personNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
        }
        isMobileBannerShowed={false}
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </Box>
  );
  // END RENDER
}
